import React, { useState, useEffect } from 'react';
import { renderContent } from './ExternalLink';
import { getVideo, getPinsForVideo, userHasPinned, deleteVideo } from '../lib/metadata';
import { useVideoStream } from '../lib/torrent';
import { getUserCommunities, pinToCommunity } from '../lib/communities';
import { loadOrCreateIdentity } from '../lib/identity';
import { isAdmin } from '../lib/moderation';
import { publishEvent } from '../lib/sync';
import { getLikeCount, hasLiked, addLikesListener } from '../lib/likes';
import { buildVideoLink } from '../lib/deepLink';
import { Comments } from './Comments';
import { ReportButton } from './ReportButton';
import { COLORS, SPACING } from '../styles/theme';

export function VideoPlayer({ videoHash, onBack, onViewUserProfile, onViewTag }: { videoHash: string; onBack: () => void; onViewUserProfile?: (userId: string) => void; onViewTag?: (tag: string) => void }) {
  const [video, setVideo] = useState<any>(null);
  const [pinCount, setPinCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [identity, setIdentity] = useState<any>(null);
  const [userCommunities, setUserCommunities] = useState<any[]>([]);
  const [showCommunityPinMenu, setShowCommunityPinMenu] = useState(false);
  const [hasPinned, setHasPinned] = useState(false);
  const [likeCount, setLikeCount] = useState(() => getLikeCount(videoHash));
  const [liked, setLiked] = useState(false);
  const { videoRef, videoUrl, streamingViaP2P, downloadProgress } = useVideoStream(videoHash);

  useEffect(() => {
    const id = loadOrCreateIdentity();
    setIdentity(id);
    setLiked(hasLiked(videoHash, id.userId));

    const communities = getUserCommunities(id.userId);
    setUserCommunities(communities);

    const v = getVideo(videoHash);
    setVideo(v);
    setPinCount(getPinsForVideo(videoHash).length);
    setHasPinned(userHasPinned(id.userId, videoHash));
    setLoading(false);
  }, [videoHash]);

  useEffect(() => {
    return addLikesListener(() => {
      setLikeCount(getLikeCount(videoHash));
    });
  }, [videoHash]);

  const [shareCopied, setShareCopied] = useState(false);
  const handleShare = () => {
    navigator.clipboard?.writeText(buildVideoLink(videoHash));
    setShareCopied(true);
    setTimeout(() => setShareCopied(false), 2000);
  };

  const handleLike = async () => {
    if (liked || !identity) return;
    setLiked(true);
    setLikeCount(c => c + 1);
    await publishEvent('like', { targetId: videoHash, targetType: 'video', userId: identity.userId });
  };

  const handleDeleteVideo = async () => {
    if (!identity || !video) return;
    if (!window.confirm('Delete this video? This cannot be undone.')) return;
    deleteVideo(video.hash, identity.userId, isAdmin(identity.publicKey));
    await publishEvent('delete_video', { hash: video.hash, userId: identity.userId });
    onBack();
  };

  const handlePinToCommunity = async (communityId: string) => {
    if (!identity) return;
    pinToCommunity(communityId, videoHash, identity.userId);
    await publishEvent('community_pin', { communityId, videoHash, pinnedBy: identity.userId, pinnedAt: Date.now() });
    setShowCommunityPinMenu(false);
  };

  if (loading) return <div style={{ padding: SPACING.xl, color: COLORS.textMuted }}>Loading...</div>;
  if (!video) return <div style={{ padding: SPACING.xl, color: COLORS.textMuted }}>Not found</div>;

  return (
    <div style={{
      border: `2px outset ${COLORS.highlight}`,
      backgroundColor: COLORS.panel,
      overflow: 'auto',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div style={{
        backgroundColor: COLORS.accent,
        color: '#fff',
        padding: `${SPACING.xs} ${SPACING.sm}`,
        fontSize: '9px',
        fontWeight: 700,
        borderBottom: `2px solid ${COLORS.highlight}`,
        userSelect: 'none'
      }}>
        Video Player
      </div>

      <div style={{ padding: SPACING.lg, backgroundColor: COLORS.bg, flex: 1, overflowY: 'auto' }}>
        <div style={{ marginBottom: SPACING.lg }}>
          <button
            onClick={onBack}
            style={{
              padding: `${SPACING.sm} ${SPACING.lg}`,
              backgroundColor: COLORS.panel,
              color: COLORS.accent,
              border: `2px outset ${COLORS.highlight}`,
              cursor: 'pointer',
              fontFamily: 'monospace',
              fontSize: '9px',
              fontWeight: 700,
              textDecoration: 'underline',
              marginBottom: SPACING.lg,
              boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.5)'
            }}
            onMouseDown={(e) => {
              e.currentTarget.style.borderStyle = 'inset';
              e.currentTarget.style.boxShadow = 'inset 1px 1px 2px rgba(0,0,0,0.8)';
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.borderStyle = 'outset';
              e.currentTarget.style.boxShadow = 'inset 0 0 0 1px rgba(0,0,0,0.5)';
            }}
          >
            ← Back
          </button>
        </div>

        <h2 style={{
          margin: `0 0 ${SPACING.lg} 0`,
          fontSize: '16px',
          fontFamily: 'monospace',
          fontWeight: 700,
          fontStyle: 'italic',
          color: COLORS.accent
        }}>
          {video.title}
        </h2>

        {videoUrl ? (
          <div style={{
            border: `2px outset ${COLORS.highlight}`,
            marginBottom: SPACING.lg,
            overflow: 'hidden',
            backgroundColor: '#000'
          }}>
            <video
              src={videoUrl}
              controls
              style={{ width: '100%', display: 'block', backgroundColor: '#000' }}
            />
          </div>
        ) : streamingViaP2P ? (
          <div style={{
            border: `2px outset ${COLORS.highlight}`,
            marginBottom: SPACING.lg,
            overflow: 'hidden',
            backgroundColor: '#000'
          }}>
            <video
              ref={videoRef}
              controls
              style={{ width: '100%', display: 'block', backgroundColor: '#000' }}
            />
            {downloadProgress !== null && downloadProgress < 100 && (
              <div style={{ padding: SPACING.sm, backgroundColor: '#000' }}>
                <div style={{ fontSize: '9px', color: COLORS.textMuted, marginBottom: SPACING.xs }}>
                  Downloading from peers... {downloadProgress}%
                </div>
                <div style={{ width: '100%', height: '4px', backgroundColor: '#222', overflow: 'hidden' }}>
                  <div style={{
                    width: `${downloadProgress}%`,
                    height: '100%',
                    backgroundColor: COLORS.accent,
                    transition: 'width 0.3s ease'
                  }} />
                </div>
              </div>
            )}
          </div>
        ) : (
          <div style={{
            border: `2px inset ${COLORS.highlight}`,
            padding: SPACING.xl,
            marginBottom: SPACING.lg,
            textAlign: 'center',
            backgroundColor: '#000',
            color: COLORS.textMuted
          }}>
            Video not available
          </div>
        )}

        <div style={{ marginBottom: SPACING.lg, display: 'flex', gap: SPACING.md, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={handleLike}
            disabled={liked}
            style={{
              padding: `${SPACING.sm} ${SPACING.lg}`,
              backgroundColor: liked ? COLORS.accent : 'transparent',
              color: liked ? '#fff' : COLORS.accent,
              border: `2px outset ${COLORS.highlight}`,
              cursor: liked ? 'default' : 'pointer',
              fontFamily: 'monospace',
              fontSize: '9px',
              fontWeight: 700,
            }}
          >
            ♥ {likeCount > 0 ? likeCount : 'Like'}
          </button>
          <button
            onClick={handleShare}
            style={{
              padding: `${SPACING.sm} ${SPACING.lg}`,
              backgroundColor: shareCopied ? COLORS.accent : 'transparent',
              color: shareCopied ? '#fff' : COLORS.accent,
              border: `2px outset ${COLORS.highlight}`,
              cursor: 'pointer', fontFamily: 'monospace', fontSize: '9px', fontWeight: 700,
            }}
          >
            {shareCopied ? '✓ copied' : '⇥ share'}
          </button>
          {(identity?.userId === video.uploader || (identity && isAdmin(identity.publicKey))) && (
            <button
              onClick={handleDeleteVideo}
              style={{
                padding: `${SPACING.sm} ${SPACING.lg}`,
                backgroundColor: 'transparent',
                color: COLORS.danger,
                border: `2px outset ${COLORS.highlight}`,
                cursor: 'pointer', fontFamily: 'monospace', fontSize: '9px', fontWeight: 700,
              }}
            >
              🗑 delete
            </button>
          )}
          {identity?.userId !== video.uploader && (
            <ReportButton targetId={videoHash} targetType="video" />
          )}
          <button
            onClick={() => setShowCommunityPinMenu(!showCommunityPinMenu)}
            style={{
              padding: `${SPACING.sm} ${SPACING.lg}`,
              backgroundColor: COLORS.accent,
              color: '#fff',
              border: `2px outset ${COLORS.highlight}`,
              cursor: 'pointer',
              fontFamily: 'monospace',
              fontSize: '9px',
              fontWeight: 700,
              textDecoration: 'underline',
              position: 'relative',
              marginRight: 0,
              boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.5)'
            }}
            onMouseDown={(e) => {
              e.currentTarget.style.borderStyle = 'inset';
              e.currentTarget.style.boxShadow = 'inset 1px 1px 2px rgba(0,0,0,0.8)';
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.borderStyle = 'outset';
              e.currentTarget.style.boxShadow = 'inset 0 0 0 1px rgba(0,0,0,0.5)';
            }}
          >
            Pin to Community
          </button>

          {showCommunityPinMenu && (
            <div style={{
              marginTop: SPACING.sm,
              border: `2px outset ${COLORS.highlight}`,
              backgroundColor: COLORS.panel,
              overflow: 'hidden'
            }}>
              {userCommunities.length === 0 ? (
                <p style={{ padding: SPACING.lg, color: COLORS.textMuted, margin: 0, fontSize: '9px' }}>
                  No communities
                </p>
              ) : (
                userCommunities.map(community => (
                  <button
                    key={community.id}
                    onClick={() => handlePinToCommunity(community.id)}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: SPACING.md,
                      backgroundColor: COLORS.bg,
                      color: COLORS.accent,
                      border: 'none',
                      borderBottom: `1px solid ${COLORS.border}`,
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontSize: '9px',
                      fontWeight: 700,
                      textDecoration: 'underline',
                      fontFamily: 'monospace'
                    }}
                  >
                    {community.name}
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        <div style={{
          border: `2px outset ${COLORS.highlight}`,
          backgroundColor: COLORS.bg,
          padding: SPACING.lg,
          marginBottom: SPACING.lg
        }}>
          <h3 style={{
            margin: `0 0 ${SPACING.lg} 0`,
            fontSize: '12px',
            fontFamily: 'monospace',
            fontWeight: 700,
            fontStyle: 'italic',
            color: COLORS.accent
          }}>
            Details
          </h3>
          <p style={{ margin: `0 0 ${SPACING.sm} 0`, fontSize: '9px', color: COLORS.textMuted }}>
            By{' '}
            <button
              onClick={() => onViewUserProfile?.(video.uploader)}
              style={{
                backgroundColor: 'transparent',
                color: COLORS.accent,
                border: 'none',
                cursor: 'pointer',
                textDecoration: 'underline',
                fontSize: 'inherit',
                fontFamily: 'inherit',
                fontWeight: 'inherit'
              }}
            >
              {video.uploader.slice(0, 8)}
            </button>
          </p>
          <p style={{ margin: `0 0 ${SPACING.sm} 0`, fontSize: '9px', color: COLORS.textMuted }}>
            {new Date(video.uploadedAt).toLocaleString()}
          </p>
          <p style={{ margin: `0 0 ${SPACING.sm} 0`, fontSize: '9px', color: COLORS.textMuted }}>
            {(video.fileSize / 1024 / 1024).toFixed(2)}MB
          </p>
          <p style={{ margin: 0, fontSize: '9px', color: COLORS.textMuted }}>
            {pinCount} pins {hasPinned && '(you pinned)'}
          </p>
        </div>

        {video.description && (
          <div style={{
            border: `2px outset ${COLORS.highlight}`,
            backgroundColor: COLORS.bg,
            padding: SPACING.lg,
            marginBottom: SPACING.lg
          }}>
            <h3 style={{
              margin: `0 0 ${SPACING.md} 0`,
              fontSize: '12px',
              fontFamily: 'monospace',
              fontWeight: 700,
              fontStyle: 'italic',
              color: COLORS.accent
            }}>
              Description
            </h3>
            <p style={{ margin: 0, fontSize: '10px', color: COLORS.text, lineHeight: 1.5 }}>
              {renderContent(video.description)}
            </p>
          </div>
        )}

        {video.tags && video.tags.length > 0 && (
          <div style={{
            border: `2px outset ${COLORS.highlight}`,
            backgroundColor: COLORS.bg,
            padding: SPACING.lg,
            marginBottom: SPACING.lg
          }}>
            <h3 style={{
              margin: `0 0 ${SPACING.md} 0`,
              fontSize: '12px',
              fontFamily: 'monospace',
              fontWeight: 700,
              fontStyle: 'italic',
              color: COLORS.accent
            }}>
              Tags
            </h3>
            <div style={{ display: 'flex', gap: SPACING.sm, flexWrap: 'wrap' }}>
              {video.tags.map((tag: string) => (
                <button
                  key={tag}
                  onClick={() => onViewTag?.(tag)}
                  style={{
                    backgroundColor: 'transparent',
                    color: COLORS.highlight,
                    border: `1px solid ${COLORS.highlight}`,
                    cursor: 'pointer',
                    padding: `${SPACING.xs} ${SPACING.sm}`,
                    fontSize: '8px',
                    fontWeight: 700,
                    fontFamily: 'monospace',
                    textDecoration: 'underline'
                  }}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        )}

        <Comments videoHash={videoHash} />
      </div>
    </div>
  );
}
